import {useI18n} from 'i18n/I18nContext'
import React from 'react'
import styled from 'styled-components'
import {Droppable, Draggable} from 'react-beautiful-dnd'
import {useTemplate} from 'TemplateEditor/TemplateProvider'
import {Section} from 'TemplateEditor/Layout/Sections/Section'
import {useEditor} from 'TemplateEditor/EditorProvider'
import {PreviewTicketBar} from 'TemplateEditor/Layout/PreviewTicketBar'
import {Template} from 'TemplateEditor/template'
import {ButtonJoinTypeId} from 'TemplateEditor/Layout/Blocks/ButtonJoin/ButtonJoin'
import {ButtonSalesTypeId} from 'TemplateEditor/Layout/Blocks/ButtonSales/ButtonSales'

/** Same 76px top offset as org template chrome in `Layout`; 0 in attendee preview. */
const EDITOR_LAYOUT_TOP_PX = 76

export function Content() {
  const {t} = useI18n()
  const {template} = useTemplate()

  const {editMode, mobileMode} = useEditor()

  const joinError =
    editMode && !hasJoinButton(template) ? (
      <ErrorText style={{whiteSpace: 'pre-line'}}>
        {t('templateEditor.joinButtonRequiredHint')}
      </ErrorText>
    ) : null

  const salesError =
    editMode &&
    hasJoinButton(template) &&
    hasSalesButtonWithoutProduct(template) ? (
      <ErrorText>{t('templateEditor.salesButtonNoProduct')}</ErrorText>
    ) : null

  return (
    <EditorPreview mobileMode={mobileMode}>
      <PreviewTicketBar />
      <Droppable
        droppableId="editor-sections"
        type="section"
        isDropDisabled={!editMode}
      >
        {(provided) => (
          <StyledDroppable
            $minHeightVhMinusPx={editMode ? EDITOR_LAYOUT_TOP_PX : 0}
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {template.sections?.length === 0 && editMode && (
              <NoSections>{t('templateEditor.emptySectionsHint')}</NoSections>
            )}

            {template.sections?.map((section, index) => {
              const sectionDragId = section.id || `section-fallback-${index}`
              return (
                <Draggable
                  isDragDisabled={!editMode}
                  key={sectionDragId}
                  draggableId={sectionDragId}
                  index={index}
                >
                  {(dragProvided) => (
                    <Section
                      provided={dragProvided}
                      section={section}
                      sectionIndex={index}
                    />
                  )}
                </Draggable>
              )
            })}

            {provided.placeholder}
          </StyledDroppable>
        )}
      </Droppable>
      {editMode && (
        <ErrorHolder>
          {joinError}
          {salesError}
        </ErrorHolder>
      )}
    </EditorPreview>
  )
}

function hasSalesButtonWithoutProduct(template: Template): boolean {
  if (!template.sections) {
    return false
  }

  for (const section of template.sections) {
    for (const blocks of Object.values(section.blocks)) {
      for (const block of blocks || []) {
        const isRightBlock = block.type === ButtonSalesTypeId
        const hasProduct =
          'productId' in block &&
          typeof block.productId === 'string' &&
          block.productId > 0
        if (isRightBlock && !hasProduct) {
          return true
        }
      }
    }
  }

  return false
}

function hasJoinButton(template: Template): boolean {
  if (!template.sections) {
    return false
  }

  for (const section of template.sections) {
    for (const blocks of Object.values(section.blocks)) {
      for (const block of blocks || []) {
        const isRightBlock = block.type === ButtonJoinTypeId
        const isVisible = block.visible
        const hasText =
          'text' in block &&
          typeof block.text === 'string' &&
          block.text.trim() !== ''
        if (isRightBlock && isVisible && hasText) {
          return true
        }
      }
    }
  }

  return false
}

const ErrorHolder = styled.div`
 position: fixed;
    bottom: 0px;
    width: 400px;
    background: red;
    color: white;
}
`

const ErrorText = styled.div`
  padding: 10px;
`

const EditorPreview = styled.div<{mobileMode: boolean}>`
  box-sizing: border-box;
  position: relative;
  margin: 0 auto;
  width: 100%;

  height: 100%;
  transition: 0.2s;
`

const StyledDroppable = styled.div<{$minHeightVhMinusPx: number}>`
  min-height: calc(100vh - ${(p) => p.$minHeightVhMinusPx}px);
`

const NoSections = styled.div`
  width: 100%;
  padding: 35px 0px;
  background: repeating-linear-gradient(
    135deg,
    #ffe4e4 0%,
    #ffe4e4 1%,
    transparent 1%,
    transparent 2%
  );
  text-align: center;
  border-bottom: 2px solid #f75b64;
  font-weight: bold;
`
